/**
 * W-030 — Users list. Stories: US-A06.
 *
 * AC1 search by name, phone or email
 * AC4 filters by stream, city, subscription status
 * CSV export of the filtered view.
 */
import { List, useTable } from '@refinedev/antd'
import { CloseOutlined, FilterOutlined } from '@ant-design/icons'
import { Button, Input, Select, Space, Table, Tag, Typography } from 'antd'
import type { CrudFilters } from '@refinedev/core'
import { useList } from '@refinedev/core'
import dayjs from 'dayjs'
import { useState } from 'react'
import { Link } from 'react-router'
import { ModelInsights, StatusTag } from '@/components'
import { downloadCsv } from '@/lib/csv'
import { useAdminTranslate } from '@/providers/i18n-provider'
import type { City, Profile, SubscriptionStatus } from '@/types'
import { useStreams } from '../subjects/useStreams'

interface ProfileRow extends Profile {
  /** From the `profiles_with_subscription` view — see TODO below. */
  subscription_status?: SubscriptionStatus | null
  subscription_expires_at?: string | null
}

export const UserListPage = () => {
  const translate = useAdminTranslate()
  const [search, setSearch] = useState('')
  const [streamId, setStreamId] = useState<string | undefined>()
  const [cityId, setCityId] = useState<string | undefined>()
  const [subscriptionStatus, setSubscriptionStatus] = useState<SubscriptionStatus | undefined>()

  const { streams } = useStreams()
  const { data: citiesData } = useList<City>({
    resource: 'cities',
    pagination: { mode: 'off' },
    sorters: [{ field: 'sort_order', order: 'asc' }],
  })
  const cities = citiesData?.data ?? []

  const buildFilters = (): CrudFilters => {
    const filters: CrudFilters = []
    if (search) {
      filters.push({
        operator: 'or',
        value: [
          { field: 'full_name', operator: 'contains', value: search },
          { field: 'phone', operator: 'contains', value: search },
          { field: 'email', operator: 'contains', value: search },
        ],
      })
    }
    if (streamId) filters.push({ field: 'stream_id', operator: 'eq', value: streamId })
    if (cityId) filters.push({ field: 'city_id', operator: 'eq', value: cityId })
    if (subscriptionStatus) {
      filters.push({ field: 'subscription_status', operator: 'eq', value: subscriptionStatus })
    }
    return filters
  }

  const { tableProps, setFilters } = useTable<ProfileRow>({
    // TODO(US-A06): a plain `profiles` select cannot filter on subscription
    // status (AC4) or search on the e-mail, which lives in auth.users. The
    // backend agent owns a `profiles_admin` view joining profiles, auth.users
    // and the live subscription; this is its working name.
    resource: 'profiles_admin',
    sorters: { initial: [{ field: 'created_at', order: 'desc' }] },
    pagination: { pageSize: 25 },
    syncWithLocation: true,
  })

  const apply = () => setFilters(buildFilters(), 'replace')
  const rows = (tableProps.dataSource ?? []) as ProfileRow[]
  const resultCount =
    (typeof tableProps.pagination === 'object' ? tableProps.pagination.total : undefined) ??
    rows.length
  const activeFilters = [
    search ? translate('admin.screens.users.filterSearch', { value: search }) : null,
    streamId
      ? translate('admin.screens.users.filterStream', {
          value: streams.find(stream => stream.id === streamId)?.name_fr ?? streamId,
        })
      : null,
    cityId
      ? translate('admin.screens.users.filterCity', {
          value: cities.find(city => city.id === cityId)?.name_fr ?? cityId,
        })
      : null,
    subscriptionStatus
      ? translate('admin.screens.users.filterSubscription', {
          value: translate(`admin.status.subscription.${subscriptionStatus}`),
        })
      : null,
  ].filter((filter): filter is string => Boolean(filter))
  const reset = () => {
    setSearch('')
    setStreamId(undefined)
    setCityId(undefined)
    setSubscriptionStatus(undefined)
    setFilters([], 'replace')
  }

  return (
    <List
      title={translate('admin.pages.users')}
      headerProps={{
        className: 'admin-list-header',
        subTitle: translate('admin.screens.users.subtitle'),
      }}
      canCreate={false}
      headerButtons={
        <Button
          onClick={() =>
            downloadCsv(translate('admin.screens.users.csvFileName'), rows, [
              { header: translate('admin.columns.name'), value: row => row.full_name },
              { header: translate('admin.columns.phone'), value: row => row.phone },
              {
                header: translate('admin.columns.stream'),
                value: row => streams.find(s => s.id === row.stream_id)?.name_fr,
              },
              {
                header: translate('admin.columns.city'),
                value: row => cities.find(c => c.id === row.city_id)?.name_fr,
              },
              {
                header: translate('admin.columns.subscription'),
                value: row => row.subscription_status,
              },
              {
                header: translate('admin.columns.expiresAt'),
                value: row => row.subscription_expires_at,
              },
              { header: translate('admin.columns.registeredAt'), value: row => row.created_at },
            ])
          }
        >
          {translate('admin.common.exportCsv')}
        </Button>
      }
    >
      <Space direction="vertical" style={{ width: '100%' }} size="middle">
        <ModelInsights model="users" />
        <div className="admin-filter-bar">
          <Space wrap>
            <Input.Search
              placeholder={translate('admin.screens.users.searchPlaceholder')}
              allowClear
              style={{ width: 300 }}
              value={search}
              onChange={event => setSearch(event.target.value)}
              onSearch={apply}
            />
            <Select
              allowClear
              placeholder={translate('admin.columns.stream')}
              style={{ width: 200 }}
              value={streamId}
              onChange={value => setStreamId(value)}
              options={streams.map(stream => ({ value: stream.id, label: stream.name_fr }))}
            />
            <Select
              allowClear
              placeholder={translate('admin.columns.city')}
              style={{ width: 200 }}
              value={cityId}
              onChange={value => setCityId(value)}
              options={cities.map(city => ({ value: city.id, label: city.name_fr }))}
            />
            <Select
              allowClear
              placeholder={translate('admin.columns.subscription')}
              style={{ width: 180 }}
              value={subscriptionStatus}
              onChange={value => setSubscriptionStatus(value)}
              options={(['active', 'expired', 'revoked'] as const).map(value => ({
                value,
                label: translate(`admin.status.subscription.${value}`),
              }))}
            />
            <Button type="primary" icon={<FilterOutlined />} onClick={apply}>
              {translate('admin.common.apply')}
            </Button>
            <Button icon={<CloseOutlined />} disabled={activeFilters.length === 0} onClick={reset}>
              {translate('admin.common.reset')}
            </Button>
          </Space>
        </div>

        <div className="admin-filter-summary">
          <Space wrap size={[6, 6]}>
            {activeFilters.length ? (
              activeFilters.map(filter => <Tag key={filter}>{filter}</Tag>)
            ) : (
              <Typography.Text type="secondary">
                {translate('admin.screens.users.allUsers')}
              </Typography.Text>
            )}
          </Space>
          <Typography.Text type="secondary">
            {translate('admin.common.resultCount', { count: resultCount })}
          </Typography.Text>
        </div>

        <Table<ProfileRow>
          {...tableProps}
          rowKey="id"
          scroll={{ x: 980 }}
          sticky
          columns={[
            {
              title: translate('admin.columns.name'),
              dataIndex: 'full_name',
              render: (value: string, row: ProfileRow) => (
                <Link to={`/users/${row.id}`}>{value}</Link>
              ),
            },
            { title: translate('admin.columns.phone'), dataIndex: 'phone' },
            {
              title: translate('admin.columns.stream'),
              dataIndex: 'stream_id',
              render: (value: string | null) =>
                streams.find(stream => stream.id === value)?.name_fr ?? '—',
            },
            {
              title: translate('admin.columns.city'),
              dataIndex: 'city_id',
              render: (value: string | null) =>
                cities.find(city => city.id === value)?.name_fr ?? '—',
            },
            {
              title: translate('admin.columns.subscription'),
              dataIndex: 'subscription_status',
              render: (value: SubscriptionStatus | null, row: ProfileRow) =>
                value ? (
                  <Space size={4}>
                    <StatusTag kind="subscription" value={value} />
                    {row.subscription_expires_at ? (
                      <span>{dayjs(row.subscription_expires_at).format('DD/MM/YYYY')}</span>
                    ) : null}
                  </Space>
                ) : (
                  <Tag>{translate('admin.common.none')}</Tag>
                ),
            },
            {
              title: translate('admin.columns.accountState'),
              dataIndex: 'deactivated_at',
              render: (value: string | null, row: ProfileRow) =>
                value ? (
                  <Tag color="error">{translate('admin.common.deactivated')}</Tag>
                ) : row.is_banned ? (
                  <Tag color="warning">{translate('admin.common.banned')}</Tag>
                ) : (
                  <Tag color="success">{translate('admin.common.active')}</Tag>
                ),
            },
            {
              title: translate('admin.columns.registeredAt'),
              dataIndex: 'created_at',
              render: (value: string) => dayjs(value).format('DD/MM/YYYY'),
            },
          ]}
        />
      </Space>
    </List>
  )
}
